import React from 'react';
import {
    Notice,
    CreateNoticeRequestDto,
    UpdateNoticeRequestDto,
    POPUP_YN_OPTIONS,
    USE_YN_OPTIONS,
    URGENT_YN_OPTIONS,
} from '../../types/notice.types';
import { useNoticeForm } from '../../hooks/useNoticeForm';
import { NoticeTargetSelector } from './NoticeTargetSelector';

interface NoticeModalProps {
    notice: Notice | null;
    onSave: (data: CreateNoticeRequestDto | UpdateNoticeRequestDto) => void;
    onClose: () => void;
    loading: boolean;
}

export const NoticeModal: React.FC<NoticeModalProps> = ({ notice, onSave, onClose, loading }) => {
    const {
        isEdit,
        isSupplierUser,
        formData,
        supplierOptions,
        optionsLoading,
        searchKeyword,
        riderFilterSuppId,
        availableTargetTypeOptions,
        availableTargetScopeOptions,
        currentOptionsLabel,
        selectedItems,
        showIndividualSelection,
        currentFilteredOptions,
        currentTotalOptions,
        handleChange,
        handleSubmit,
        handleAddTarget,
        handleRemoveTarget,
        handleSelectAll,
        handleClearAll,
        setSearchKeyword,
        setRiderFilterSuppId,
    } = useNoticeForm(notice, onSave);

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{isEdit ? '공지사항 수정' : '공지사항 등록'}</h3>
                    <button className="modal-close" onClick={onClose}>&times;</button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        <div className="form-group">
                            <label>제목 <span className="required">*</span></label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                placeholder="공지사항 제목을 입력하세요"
                                maxLength={100}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>내용 <span className="required">*</span></label>
                            <textarea
                                name="content"
                                value={formData.content}
                                onChange={handleChange}
                                rows={6}
                                placeholder="공지사항 내용을 입력하세요"
                                required
                            />
                        </div>

                        {formData.targetType === 'A' && (
                            <div className="form-group">
                                <div className="alert alert-info-blue">
                                    <strong>전체 공지</strong>는 모든 <strong>협력사</strong>와 <strong>라이더</strong>에게 전달됩니다.
                                    (운영 중, 계약 중 상태만 해당)
                                </div>
                            </div>
                        )}

                        {formData.targetType === 'SR' && (
                            <div className="form-group">
                                <div className="alert alert-info-amber">
                                    <strong>협력사 + 라이더</strong>: 선택한 협력사와 해당 협력사에 소속된 모든 라이더에게 동일한 내용이 전달됩니다.
                                </div>
                            </div>
                        )}

                        <div className="form-row">
                            <div className="form-group">
                                <label>대상 타입 <span className="required">*</span></label>
                                <select
                                    name="targetType"
                                    value={formData.targetType}
                                    onChange={handleChange}
                                    disabled={isSupplierUser}
                                >
                                    {availableTargetTypeOptions.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                                {isSupplierUser && (
                                    <small className="form-hint">협력사 계정은 라이더에게만 공지를 보낼 수 있습니다.</small>
                                )}
                            </div>

                            <div className="form-group">
                                <label>대상 범위 <span className="required">*</span></label>
                                <select
                                    name="targetScope"
                                    value={formData.targetScope}
                                    onChange={handleChange}
                                    disabled={formData.targetType === 'A' || formData.targetType === 'R'}
                                >
                                    {availableTargetScopeOptions.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                                {(formData.targetType === 'A' || formData.targetType === 'R') && (
                                    <small className="form-hint">
                                        {formData.targetType === 'A'
                                            ? '전체 공지는 모든 대상에게 전달됩니다.'
                                            : '라이더 공지는 전체 라이더에게 전달됩니다.'}
                                    </small>
                                )}
                            </div>
                        </div>

                        {showIndividualSelection && (
                            <div className="form-group">
                                <label>{currentOptionsLabel} 선택 <span className="required">*</span></label>
                                <NoticeTargetSelector
                                    targetType={formData.targetType}
                                    targetIds={formData.targetIds}
                                    supplierOptions={supplierOptions}
                                    optionsLoading={optionsLoading}
                                    searchKeyword={searchKeyword}
                                    riderFilterSuppId={riderFilterSuppId}
                                    currentOptionsLabel={currentOptionsLabel}
                                    selectedItems={selectedItems}
                                    currentFilteredOptions={currentFilteredOptions}
                                    currentTotalOptions={currentTotalOptions}
                                    onSearchChange={setSearchKeyword}
                                    onRiderFilterChange={setRiderFilterSuppId}
                                    onAddTarget={handleAddTarget}
                                    onRemoveTarget={handleRemoveTarget}
                                    onSelectAll={handleSelectAll}
                                    onClearAll={handleClearAll}
                                />
                            </div>
                        )}

                        <div className="form-row">
                            <div className="form-group">
                                <label>게시 시작</label>
                                <input
                                    type="datetime-local"
                                    name="startDate"
                                    value={formData.startDate}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-group">
                                <label>게시 종료</label>
                                <input
                                    type="datetime-local"
                                    name="endDate"
                                    value={formData.endDate}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>팝업 표시</label>
                                <select name="popupYn" value={formData.popupYn} onChange={handleChange}>
                                    {POPUP_YN_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* 긴급 공지 - 본사 사용자만 설정 가능 */}
                            {!isSupplierUser && (
                                <div className="form-group">
                                    <label>긴급 공지</label>
                                    <select name="urgentYn" value={formData.urgentYn} onChange={handleChange}>
                                        {URGENT_YN_OPTIONS.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                    <small className="form-hint">긴급 공지는 빨간색으로 강조 표시됩니다.</small>
                                </div>
                            )}

                            {isEdit && (
                                <div className="form-group">
                                    <label>사용 여부</label>
                                    <select name="useYn" value={formData.useYn} onChange={handleChange}>
                                        {USE_YN_OPTIONS.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>취소</button>
                        <button type="submit" className="btn btn-primary" disabled={loading}>
                            {loading ? '처리중...' : isEdit ? '수정' : '등록'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
